import {
  Hub,
  Logger,
  QUERY_PARAMS,
  HubCallback,
  useSessionStorage,
  appAuthenticator,
  ROUTES,
  AmplifyError,
  APIClients,
  ApiClientType,
  IntegrationsAPIClient
} from '@futrli/shared-services';
import { AnyError } from '@futrli/errors';
import { UILoader } from '@futrli/ui-components';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { snakeCase } from 'lodash';

import { FederatedSignInStorage, FederatedSignInError } from './types';
import { getUserLoggedInAfterSSU, getUserLoggedInAfterSSO } from './redux-utils/actions';
import { matchRegisterPath } from '../utils/route-utils';
import {
  FEDERATED_SIGN_IN_STORE_KEY,
  ALREADY_FOUND_AN_ENTRY_ERROR_MSG,
  FEDERATED_SIGN_IN_GENERIC_ERROR_CODE
} from './constants';
import { useGetRedirectInfoFromQueryParam } from '../auth/components/use-get-redirect-info-from-query-param';
import { SESSION_STORAGE_REDIRECT_PATH } from '../auth/containers/auth-container';
import { checkIfAuthenticated } from '../auth/service';
import { StoreState } from '../core/types';

/**
 * Handles the redirect response from a Federated Identity HostedUI.
 */
export const FederatedSignInRedirectManager: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [storage, store, clearStorage] = useSessionStorage<FederatedSignInStorage | null>(
    FEDERATED_SIGN_IN_STORE_KEY,
    null
  );

  if (!storage) {
    throw new Error('Federated Sign In data needs to be defined');
  }

  const [signInError, storeSignInError] = useSessionStorage<FederatedSignInError | null>(
    FEDERATED_SIGN_IN_STORE_KEY,
    null,
    true
  );
  const [isRetriggeringFlow, setIsRetriggeringFlow] = useState(false);
  const { isLoadingRedirectInfo, redirectInfo, onRedirect } = useGetRedirectInfoFromQueryParam(
    storage.originLocationSearch
  );
  const isRegisterPath = matchRegisterPath(storage.originLocationPathname);
  const errorQueryParams = getErrorFromQueryParams(location.search);
  const hasToRetriggerFlow = shouldRetriggerFederatedSignInFlow(storage, isRetriggeringFlow, errorQueryParams);
  const isAuthenticated = useSelector((state: StoreState) => checkIfAuthenticated(state.authReducers));
  // Dedupe sign-in handling between the Hub event path and the
  // already-authenticated fallback path below.
  const hasHandledSignIn = useRef(false);

  useEffect(() => {
    APIClients.setApiClient(ApiClientType.Aws);
    IntegrationsAPIClient.setApiClient(ApiClientType.Aws);
  }, []);

  /**
   * Handles the OAuth2.0 success flow and gets the user into the app.
   */
  useEffect(() => {
    if (errorQueryParams || isLoadingRedirectInfo) {
      return;
    }

    const handleRedirect = async () => {
      if (redirectInfo) {
        return await onRedirect();
      }

      let pathToRedirect = ROUTES.ROOT_ROUTE;
      if (isRegisterPath) {
        pathToRedirect = `${ROUTES.ONBOARDING_ROUTE}${storage.originLocationSearch}`;
      } else {
        const sessionStoragePathToRedirect = sessionStorage.getItem(SESSION_STORAGE_REDIRECT_PATH);
        if (sessionStoragePathToRedirect) {
          sessionStorage.removeItem(SESSION_STORAGE_REDIRECT_PATH);
          pathToRedirect = sessionStoragePathToRedirect;
        }
      }

      navigate(pathToRedirect);
    };

    const handleSignIn = () => {
      if (hasHandledSignIn.current) {
        return;
      }
      hasHandledSignIn.current = true;

      const getUserLoggedInAction = isRegisterPath ? getUserLoggedInAfterSSU : getUserLoggedInAfterSSO;

      dispatch(getUserLoggedInAction(handleRedirect, storage));
      if (isRegisterPath) {
        return;
      }
      return clearStorage();
    };

    const handleRejectSignIn = (hubEvent: string, error: AnyError) => {
      Logger.error(error);

      storeSignInError({ code: snakeCase(hubEvent) });
    };

    const authHubCallback: HubCallback = ({ payload: { event, data } }) => {
      switch (event) {
        case 'signedIn':
        case 'signInWithRedirect':
          return handleSignIn();
        case 'signInWithRedirect_failure':
          return handleRejectSignIn(event, data as AnyError);
        default:
          return;
      }
    };

    const unsubscribe = Hub.listen('auth', authHubCallback);

    /*
     * Fallback for aws-amplify v6: the OAuth code-exchange can complete
     * (and the `signInWithRedirect` Hub event fire) before this listener is
     * attached, in which case the event is lost. If the user is already
     * authenticated when this effect runs, drive `handleSignIn` directly.
     */
    if (isAuthenticated) {
      handleSignIn();
    }

    return () => {
      unsubscribe();
    };
  }, [errorQueryParams, isLoadingRedirectInfo, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles if the flow needs to be re-triggered due to the
   * following amplify bug linking accounts.
   * @see https://crunchboards.atlassian.net/browse/FUT-8105
   */
  useEffect(() => {
    if (!hasToRetriggerFlow) {
      return;
    }

    const retriggerSignInFlow = async () => {
      try {
        setIsRetriggeringFlow(true);
        store({ ...storage, federatedSignInRetriggered: 'true' });
        await appAuthenticator.federatedSignIn({ customProvider: storage.federatedIdentityProvider });
      } catch (error) {
        const err = error as AmplifyError;
        Logger.error(String(error));
        if (typeof err !== 'string') {
          storeSignInError({
            code: err.code || err.name,
            message: err.message
          });
        }
        setIsRetriggeringFlow(false);
      }
    };
    retriggerSignInFlow();
  }, [hasToRetriggerFlow]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles any error catched during the federated sign in flow.
   */
  useEffect(() => {
    if (isRetriggeringFlow || hasToRetriggerFlow || (!errorQueryParams && !signInError)) {
      return;
    }

    if (!signInError) {
      storeSignInError(errorQueryParams);
    }

    navigate([storage.originLocationPathname, storage.originLocationSearch].join(''));
  }, [signInError, errorQueryParams, hasToRetriggerFlow]); // eslint-disable-line react-hooks/exhaustive-deps

  return <UILoader isFull={true} />;
};

/**
 * Determines from query-param error and storage whether federated sign in has to be
 * retriggered or not.
 */
export const shouldRetriggerFederatedSignInFlow = (
  storage: FederatedSignInStorage,
  isRetriggeringFlow: boolean,
  error: FederatedSignInError | null
) =>
  !isRetriggeringFlow &&
  Boolean(error?.message?.includes(ALREADY_FOUND_AN_ENTRY_ERROR_MSG)) &&
  storage.federatedSignInRetriggered !== 'true';

export const getErrorFromQueryParams = (locationSearch: Location['search']): FederatedSignInError | null => {
  const queryParams = queryString.parse(locationSearch);
  const errorCode = queryParams[QUERY_PARAMS.ERROR] as string;
  const errorDescription = queryParams[QUERY_PARAMS.ERROR_DESCRIPTION] as string;

  if (!errorCode && !errorDescription) {
    return null;
  }

  return {
    code: errorCode || FEDERATED_SIGN_IN_GENERIC_ERROR_CODE,
    message: errorDescription
  };
};
